import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Tabs, TabsContent } from '../../components/ui';
import { SplitViewTabBar } from '../../components/generic/SplitViewWithTabs';
import NonConformityReportDetailsTab from './components/tabs/NonConformityReportDetailsTab';
import NonConformityReportTimelineTab from './components/tabs/NonConformityReportTimelineTab';
import NonConformityReportAuditLogTab from './components/tabs/NonConformityReportAuditLogTab';
import { useFlexibleList } from '../../hooks/useSWR';

/**
 * Custom Split View Panel for Non-Conformity Report module with responsive tab carousel
 * 
 * Features:
 * - Smart Detection: Automatically calculates how many tabs can fit based on drawer width
 * - Config-Based: Uses actual drawerWidth from module config for precise calculations
 * - Clean Navigation: Only shows Previous/Next arrows when needed
 * - No Overflow: All elements stay within panel boundaries
 * - Responsive: Adapts to any drawer width configuration
 * 
 * Tabs:
 * - Details: Non-conformity report information and file attachments
 * - Timeline: Non-conformity report activity timeline
 * - Audit Log: Complete audit trail for the non-conformity report
 */
const CustomNonConformityReportSplitView = ({ 
  isOpen, 
  selectedRecord, 
  selectedRecordFiles, 
  config, 
  fields, 
  labels,
  onClose,
  onRecordUpdate // Callback to refresh Non-Conformity Report data
}) => {
  const { theme } = useTheme();

  // Fetch Non-Conformity Report timeline entries count
  const { data: timelineEntries = [] } = useFlexibleList(
    selectedRecord?.id ? `/non-conformity-report-timeline-entry/by-non-conformity-report/${selectedRecord.id}` : null
  );
  
  // Fetch Non-Conformity Report audit logs using master entity endpoint (includes all related entities)
  const { data: auditLogs = [] } = useFlexibleList(
    selectedRecord?.id ? `/audit-log/master/${config?.features?.masterEntityTypeId || 5}/${selectedRecord.id}` : null
  );
  
  // Fetch file attachments count (if the endpoint exists)
  const { data: fileAttachments = [] } = useFlexibleList(
    selectedRecord?.id ? `/file-attachment/entity/${config?.entityTypeId || 5}/${selectedRecord.id}` : null
  );
  
  // Define all tabs with their data
  const allTabs = [
    {
      value: "details",
      label: "Details",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      badge: fileAttachments.length,
      badgeColor: "bg-green-600"
    },
    {
      value: "timeline",
      label: "Timeline",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      badge: timelineEntries.length,
      badgeColor: "bg-orange-600"
    },
    {
      value: "audit-log",
      label: "Audit Log",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      badge: auditLogs.length,
      badgeColor: "bg-red-600"
    }
  ];
  
  if (!isOpen) return null;

  return (
    <div 
      className={cn("hidden lg:block min-h-0 h-full pb-2")}
      style={{ width: config.ui?.drawerWidth || '420px' }}
    >
      <div className={cn("min-h-0 h-full rounded-xl border flex flex-col", theme.border.light, theme.colors.surface, theme.shadow.md)}>
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className={cn("text-sm font-semibold", theme.text.primary)}>
            {selectedRecord ? `NCR - ${selectedRecord.code || selectedRecord.id}` : "Select a Non-Conformity Report to view details"}
          </div>
          <button
            onClick={onClose}
            className={cn("p-1 rounded hover:opacity-80", theme.text.secondary)}
            title={labels.closeText || 'Close'}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Tab Content */}
        {selectedRecord ? (
          <Tabs defaultValue="details" className="flex-1 flex flex-col min-h-0 px-3 py-2">
            <SplitViewTabBar tabs={allTabs} />
            
            <div className="flex-1 flex flex-col min-h-0">
              {/* Details Tab */}
              <TabsContent value="details" className="mt-0 flex-1 flex flex-col min-h-0">
                <NonConformityReportDetailsTab 
                  selectedRecord={selectedRecord}
                  selectedRecordFiles={selectedRecordFiles}
                  config={config}
                  fields={fields}
                />
              </TabsContent>

              {/* Timeline Tab */}
              <TabsContent value="timeline" className="mt-0 flex-1 flex flex-col min-h-0">
                <NonConformityReportTimelineTab 
                  selectedRecord={selectedRecord}
                />
              </TabsContent>

              {/* Audit Log Tab */}
              <TabsContent value="audit-log" className="mt-0 flex-1 flex flex-col min-h-0">
                <NonConformityReportAuditLogTab 
                  selectedRecord={selectedRecord}
                />
              </TabsContent>
            </div>
          </Tabs>
        ) : (
          <div className={cn("p-4 text-sm", theme.text.secondary)}>
            {labels.noSelectionText || "No Non-Conformity Report selected"}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomNonConformityReportSplitView;
